import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Container, Typography, Paper, Stack, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Button, Avatar, Chip, Alert,
  Card, CardContent, Divider, useTheme, alpha,
  Backdrop, CircularProgress, Snackbar,
  IconButton, Tooltip,
  ListSubheader
} from '@mui/material';
import {
  AddShoppingCart as SaleIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  AttachMoney as MoneyIcon,
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  CurrencyExchange as NairaIcon,
  Restaurant as SupervisorIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { baseUrl, config, requestID } from "../../../common/utils";

// Types based on API response
interface SaleItem {
  item_id: string;
  item_name: string;
  category_id: string;
  category_name: string;
}

interface ApiResponse {
  status: boolean;
  message: string;
  data: {
    status: boolean;
    data: SaleItem[];
  };
}

interface Staff {
  user_id: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  type: 'waiter' | 'supervisor';
}

const RecordSale: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  
  // Form states
  const [itemId, setItemId] = useState<string>('');
  const [itemName, setItemName] = useState<string>('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [categoryName, setCategoryName] = useState<string>('');
  const [qtySold, setQtySold] = useState<string>('');
  const [cost, setCost] = useState<string>('');
  const [reportedBy, setReportedBy] = useState<string>('');
  const [reportedOn, setReportedOn] = useState<Date>(new Date());
  
  // Data stores
  const [saleItems, setSaleItems] = useState<SaleItem[]>([]);
  const [categories, setCategories] = useState<SaleItem[]>([]);
  const [waiters, setWaiters] = useState<Staff[]>([]);
  const [supervisors, setSupervisors] = useState<Staff[]>([]);
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  
  // Custom item/category input
  const [isCustomItem, setIsCustomItem] = useState<boolean>(false);
  const [customItemName, setCustomItemName] = useState<string>('');
  const [isCustomCategory, setIsCustomCategory] = useState<boolean>(false);
  const [customCategoryName, setCustomCategoryName] = useState<string>('');
  
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Format date to YYYY-MM-DD
  const formatDate = (date: Date): string => {
    return format(date, 'yyyy-MM-dd');
  };

  // Format currency with Naira symbol
  const formatNaira = (amount: number): string => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
      currencyDisplay: 'symbol'
    }).format(amount).replace('NGN', '₦');
  };

  // Fetch sale items
  const fetchSaleItems = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post<ApiResponse>(
        `${baseUrl()}BeverageSupervisors/getSalesItems`, 
        formData, 
        config
      );
      
      console.log("Sale items response:", response.data);

      if (response.data.status) {
        // Access the nested data structure
        const items = response.data.data?.data || [];
        setSaleItems(items);
        
        // Extract unique categories from items (using a Map to remove duplicates)
        const uniqueCategoriesMap = new Map();
        items.forEach((item: SaleItem) => {
          if (!uniqueCategoriesMap.has(item.category_id)) {
            uniqueCategoriesMap.set(item.category_id, {
              category_id: item.category_id,
              category_name: item.category_name
            });
          }
        });
        
        // Convert Map to array
        const uniqueCategories = Array.from(uniqueCategoriesMap.values());
        setCategories(uniqueCategories);
        
        console.log("Extracted categories:", uniqueCategories);
      } else {
        showNotification('Failed to fetch sale items', 'error');
      }
    } catch (error) {
      console.error("Fetch sale items error:", error);
      showNotification('Error fetching sale items', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch waiters
  const fetchWaiters = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/getWaiters`, formData, config);
      
      if (response.data.status) {
        const waitersData = response.data.data || [];
        const formattedWaiters = waitersData.map((waiter: any) => ({
          ...waiter,
          fullName: waiter.fullName || `${waiter.firstName || ''} ${waiter.lastName || ''}`.trim(),
          type: 'waiter' as const
        }));
        setWaiters(formattedWaiters);
      }
    } catch (error) {
      console.error("Fetch waiters error:", error);
    }
  };

  // Fetch food & beverage supervisors
  const fetchSupervisors = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/getFoodBeverageSupervisors`, formData, config);
      
      console.log("Supervisors response:", response.data);

      if (response.data.status) {
        const supervisorsData = response.data.data || [];
        const formattedSupervisors = supervisorsData.map((sup: any) => ({
          ...sup,
          fullName: sup.fullName || `${sup.firstName || ''} ${sup.lastName || ''}`.trim(),
          type: 'supervisor' as const
        }));
        setSupervisors(formattedSupervisors);
      }
    } catch (error) {
      console.error("Fetch supervisors error:", error);
    }
  };

  // Combine all staff for the dropdown
  useEffect(() => {
    const combined = [...waiters, ...supervisors];
    setAllStaff(combined);
  }, [waiters, supervisors]);

  // Get staff name
  const getStaffName = (staff: Staff): string => {
    return staff.fullName || `${staff.firstName || ''} ${staff.lastName || ''}`.trim();
  };

  // Get staff role label
  const getStaffRole = (type: string): string => {
    return type === 'waiter' ? 'Waiter' : 'F&B Supervisor';
  };

  // Handle item selection
  const handleItemChange = (event: any) => {
    const selectedId = event.target.value;
    setItemId(selectedId);
    
    if (selectedId === 'custom') {
      setIsCustomItem(true);
      setItemName('');
      setCategoryId('');
      setCategoryName('');
    } else {
      setIsCustomItem(false);
      const selected = saleItems.find(item => item.item_id === selectedId);
      if (selected) {
        setItemName(selected.item_name);
        setCategoryId(selected.category_id);
        setCategoryName(selected.category_name);
      }
    }
  };

  // Handle category selection
  const handleCategoryChange = (event: any) => {
    const selectedId = event.target.value;
    setCategoryId(selectedId);
    
    if (selectedId === 'custom') {
      setIsCustomCategory(true);
      setCategoryName('');
    } else {
      setIsCustomCategory(false);
      const selected = categories.find(cat => cat.category_id === selectedId);
      setCategoryName(selected?.category_name || '');
    }
  };

  // Calculate total cost
  const calculateTotalCost = (): number => {
    const qty = parseFloat(qtySold) || 0;
    const price = parseFloat(cost) || 0;
    return qty * price;
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validation
    if (isCustomItem && !customItemName) {
      showNotification('Please enter item name', 'warning');
      return;
    }
    
    if (!isCustomItem && !itemId) {
      showNotification('Please select an item', 'warning');
      return;
    }

    if (isCustomCategory && !customCategoryName) {
      showNotification('Please enter category name', 'warning');
      return;
    }

    if (!categoryId && !isCustomCategory) {
      showNotification('Please select a category', 'warning');
      return;
    }

    if (!qtySold || parseFloat(qtySold) <= 0) {
      showNotification('Please enter valid quantity', 'warning');
      return;
    }

    if (!cost || parseFloat(cost) <= 0) {
      showNotification('Please enter valid cost/price', 'warning');
      return;
    }

    if (!reportedBy) {
      showNotification('Please select who reported the sale', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      // Generate IDs for custom items/categories
      const finalItemId = isCustomItem ? `custom_item_${Date.now()}` : itemId;
      const finalItemName = isCustomItem ? customItemName : itemName;
      const finalCategoryId = isCustomCategory ? `custom_cat_${Date.now()}` : categoryId;
      const finalCategoryName = isCustomCategory ? customCategoryName : categoryName;
      
      formData.append("item_id", finalItemId);
      formData.append("item_name", finalItemName);
      formData.append("category_id", finalCategoryId);
      formData.append("category_name", finalCategoryName);
      formData.append("qty_sold", qtySold);
      formData.append("cost", cost);
      formData.append("reported_by", reportedBy);
      formData.append("reported_on", formatDate(reportedOn));

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/recordSale`, formData, config);
      
      if (response.data.status) {
        showNotification('Sale recorded successfully', 'success');
        resetForm();
        // Refresh sale items to show newly added items
        await fetchSaleItems();
      } else {
        showNotification(response.data.message || 'Failed to record sale', 'error');
      }
    } catch (error: any) {
      console.error("Record sale error:", error);
      showNotification(error.response?.data?.message || 'Error recording sale', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setItemId('');
    setItemName('');
    setCategoryId('');
    setCategoryName('');
    setQtySold('');
    setCost('');
    setReportedBy('');
    setReportedOn(new Date());
    setIsCustomItem(false);
    setCustomItemName('');
    setIsCustomCategory(false);
    setCustomCategoryName('');
  };

  useEffect(() => {
    fetchSaleItems();
    fetchWaiters();
    fetchSupervisors();
  }, []);

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <IonPage>
      <IonContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
            
            {/* Header */}
            <Paper 
              elevation={0}
              sx={{ 
                borderBottom: `1px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: 'background.paper'
              }}
            >
              <Container maxWidth="xl">
                <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                  <IconButton onClick={() => history.goBack()}>
                    <BackIcon />
                  </IconButton>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: theme.palette.success.main
                    }}
                  >
                    <SaleIcon />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="h5" fontWeight={600}>
                      Record Sale
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enter daily sales transactions
                    </Typography>
                  </Box>
                  <Tooltip title="Refresh Items">
                    <IconButton onClick={fetchSaleItems} disabled={loading}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Container>
            </Paper>

            <Container maxWidth="lg" sx={{ py: 3 }}>
              
              {/* Main Form */}
              <Paper sx={{ p: 4 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Sale Information
                </Typography>
                <Divider sx={{ mb: 4 }} />

                <Grid container spacing={3}>
                  
                  {/* Sale Date */}
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Sale Date"
                      value={reportedOn}
                      onChange={(date) => date && setReportedOn(date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          required: true
                        }
                      }}
                    />
                  </Grid>

                  {/* Reported By - Combined Dropdown with Waiters and Supervisors */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small" required>
                      <InputLabel>Reported By</InputLabel>
                      <Select
                        value={reportedBy}
                        onChange={(e) => setReportedBy(e.target.value)}
                        label="Reported By"
                        renderValue={(selected) => {
                          const staff = allStaff.find(s => s.user_id === selected);
                          return staff ? (
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Avatar sx={{ width: 24, height: 24, bgcolor: staff.type === 'waiter' ? theme.palette.info.main : theme.palette.success.main }}>
                                {staff.type === 'waiter' ? <PersonIcon fontSize="small" /> : <SupervisorIcon fontSize="small" />}
                              </Avatar>
                              <span>{getStaffName(staff)}</span>
                              <Chip 
                                label={getStaffRole(staff.type)}
                                size="small"
                                sx={{ 
                                  ml: 1,
                                  bgcolor: staff.type === 'waiter' ? alpha(theme.palette.info.main, 0.1) : alpha(theme.palette.success.main, 0.1),
                                  color: staff.type === 'waiter' ? theme.palette.info.main : theme.palette.success.main,
                                  fontSize: '0.7rem',
                                  height: 20
                                }}
                              />
                            </Stack>
                          ) : '';
                        }}
                      >
                        {/* Waiters Section */}
                        {waiters.length > 0 && [
                          <ListSubheader key="waiters-header" sx={{ bgcolor: 'transparent', fontWeight: 600, color: theme.palette.info.main }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <PersonIcon fontSize="small" />
                              <span>WAITERS</span>
                            </Stack>
                          </ListSubheader>,
                          ...waiters.map(waiter => (
                            <MenuItem key={waiter.user_id} value={waiter.user_id}>
                              <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.info.main }}>
                                  <PersonIcon fontSize="small" />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body1">
                                    {getStaffName(waiter)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Waiter
                                  </Typography>
                                </Box>
                              </Stack>
                            </MenuItem>
                          )),
                          <Divider key="waiters-divider" sx={{ my: 1 }} />
                        ]}

                        {/* Supervisors Section */}
                        {supervisors.length > 0 && [
                          <ListSubheader key="supervisors-header" sx={{ bgcolor: 'transparent', fontWeight: 600, color: theme.palette.success.main }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <SupervisorIcon fontSize="small" />
                              <span>FOOD & BEVERAGE SUPERVISORS</span>
                            </Stack>
                          </ListSubheader>,
                          ...supervisors.map(supervisor => (
                            <MenuItem key={supervisor.user_id} value={supervisor.user_id}>
                              <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.success.main }}>
                                  <SupervisorIcon fontSize="small" />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body1">
                                    {getStaffName(supervisor)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    F&B Supervisor
                                  </Typography>
                                </Box>
                              </Stack>
                            </MenuItem>
                          ))
                        ]}

                        {allStaff.length === 0 && (
                          <MenuItem disabled>No staff available</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Item Selection */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small" disabled={isCustomItem}>
                      <InputLabel>Select Item</InputLabel>
                      <Select
                        value={itemId}
                        onChange={handleItemChange}
                        label="Select Item"
                      >
                        <MenuItem value="custom" sx={{ color: theme.palette.primary.main }}>
                          <em>+ Add Custom Item</em>
                        </MenuItem>
                        {saleItems.map(item => (
                          <MenuItem key={item.item_id} value={item.item_id}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                              <span>{item.item_name}</span>
                              <Chip 
                                label={item.category_name || 'Uncategorized'}
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ ml: 2 }}
                              />
                            </Stack>
                          </MenuItem>
                        ))}
                        {saleItems.length === 0 && (
                          <MenuItem disabled>No items available</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Custom Item Name */}
                  {isCustomItem && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Custom Item Name"
                        value={customItemName}
                        onChange={(e) => setCustomItemName(e.target.value)}
                        placeholder="Enter new item name"
                        required
                      />
                    </Grid>
                  )}

                  {/* Category Selection */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small" disabled={isCustomCategory}>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={categoryId}
                        onChange={handleCategoryChange}
                        label="Category"
                      >
                        <MenuItem value="custom" sx={{ color: theme.palette.primary.main }}>
                          <em>+ Add Custom Category</em>
                        </MenuItem>
                        {categories.map(cat => (
                          <MenuItem key={cat.category_id} value={cat.category_id}>
                            {cat.category_name}
                          </MenuItem>
                        ))}
                        {categories.length === 0 && (
                          <MenuItem disabled>No categories available</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Custom Category Name */}
                  {isCustomCategory && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Custom Category Name"
                        value={customCategoryName}
                        onChange={(e) => setCustomCategoryName(e.target.value)}
                        placeholder="Enter new category name"
                        required
                      />
                    </Grid>
                  )}

                  {/* Quantity Sold */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Quantity Sold"
                      type="number"
                      value={qtySold}
                      onChange={(e) => setQtySold(e.target.value)}
                      InputProps={{ inputProps: { min: 1, step: 0.01 } }}
                      placeholder="Enter quantity"
                      required
                    />
                  </Grid>

                  {/* Cost per Unit */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Price per Unit (₦)"
                      type="number"
                      value={cost}
                      onChange={(e) => setCost(e.target.value)}
                      // InputProps={{
                      //   startAdornment: <NairaIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />,
                      //   inputProps: { min: 0.01, step: 0.01 }
                      // }}
                      placeholder="Enter price in Naira"
                      required
                    />
                  </Grid>

                  {/* Total Cost (Calculated) */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Total (₦)"
                      value={formatNaira(calculateTotalCost())}
                      // InputProps={{
                      //   readOnly: true,
                      //   startAdornment: <NairaIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                      // }}
                      variant="filled"
                    />
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button variant="outlined" onClick={resetForm}>
                        Reset
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<SaveIcon />}
                        onClick={handleSubmit}
                        disabled={submitting}
                        size="large"
                      >
                        {submitting ? 'Saving...' : 'Record Sale'}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              {/* Info Card */}
              <Paper sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <InfoIcon color="info" />
                  <Typography variant="body2" color="text.secondary">
                    Sales records will be used for reporting and inventory tracking. 
                    You can add custom items and categories if they don't exist in the system.
                  </Typography>
                </Stack>
              </Paper>
            </Container>

            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                {snackbar.message}
              </Alert>
            </Snackbar>

            {/* Loading Overlay */}
            <Backdrop
              sx={{
                color: '#fff',
                zIndex: theme.zIndex.drawer + 1,
                backdropFilter: 'blur(3px)'
              }}
              open={submitting || loading}
            >
              <Stack spacing={2} alignItems="center">
                <CircularProgress color="inherit" />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {submitting ? 'Recording sale...' : 'Loading...'}
                </Typography>
              </Stack>
            </Backdrop>

          </Box>
        </LocalizationProvider>
      </IonContent>
    </IonPage>
  );
};

export default RecordSale;